// @flow strict-local

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import * as api from 'src/utils/api-no-store';

import useSchedulerEvent from './useSchedulerEvent';
import parseISO from 'date-fns/parseISO';
import ActionPrompt from './action-prompt.jsx';

import {Button} from 'src/components/lib/new-button';
import classify from '@spaced-out/ui-design-system/lib/utils/classify';
import {SadCalendar} from 'src/components/scheduler/auth-landing-page.jsx';
import {
  Container,
  DonePage,
  NotFoundPage,
  CantCancelPage,
  BookingBody,
} from './common.jsx';
import {useIsMobile} from '../useIsMobile';
import {getLinkOwner, getLinkOwnerLanguage} from './utils';
import {isLanguageSupported} from 'src/utils/language-support';

import css from '../booking.css';


export const CancelApp = ({
  eventId,
  timezone,
}: {
  eventId: string,
  timezone: string,
}): React.Node => {
  const isMobile = useIsMobile();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const eventDataResource = useSchedulerEvent(eventId);
  const [reason, setReason] = React.useState('');
  const [bookingCanceled, setCanceled] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [linkOwnerLanguage, setLinkOwnerLanguage] = React.useState(
    labelI18nInstance.language,
  );

  const url = new URL(window.location);
  const params = Object.fromEntries(url.searchParams.entries());
  const identificationNeeded = !params.attendee;

  React.useEffect(() => {
    const updateLanguage = async () => {
      const owner = await getLinkOwner(eventId, params);

      if (!eventDataResource.result) {
        return;
      }

      const userLanguage = getLinkOwnerLanguage({
        linkOwner: owner,
        userId: params.aid,
        eventData: eventDataResource.result,
      });

      if (
        userLanguage &&
        isLanguageSupported(userLanguage) &&
        userLanguage !== linkOwnerLanguage
      ) {
        setLinkOwnerLanguage(userLanguage);
      }
    };

    updateLanguage();
  }, [eventDataResource.result]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    // $FlowFixMe - api.del poorly typed
    await api.del(`scheduler/event/${eventId}`, {
      reason,
      viewer_timezone: timezone,
      attendee: params.attendee,
    });
    setSubmitting(false);
    setCanceled(true);
  };

  if (eventDataResource.isLoading) {
    return null;
  }
  const {brand_settings} = eventDataResource.result || {};

  const {did_cancellation_notice_pass = false} = eventDataResource.result || {};

  const originallyScheduledDate = eventDataResource.result?.event_start
    ? parseISO(eventDataResource.result.event_start)
    : new Date();

  return (
    <Container
      brand={brand_settings}
      persistLanguage={false}
      language={linkOwnerLanguage}
    >
      <BookingBody
        className={classify({
          [css.mobileOnly]: isMobile,
          [css.desktopOnly]: !isMobile,
          [css.identificationWrapper]: identificationNeeded,
        })}
      >
        {eventDataResource.error || did_cancellation_notice_pass ? (
          did_cancellation_notice_pass ? (
            <CantCancelPage
              cancellationPolicy={eventDataResource.result?.cancellation_policy}
            />
          ) : (
            <NotFoundPage />
          )
        ) : bookingCanceled ? (
          <DonePage>
            <SadCalendar />
            <p className={css.successText}>{`${t(
              'Your_meeting_has_been_canceled',
              'Your meeting has been canceled',
            )}.`}</p>
          </DonePage>
        ) : identificationNeeded ? (
          <ActionPrompt
            eventId={eventId}
            event={eventDataResource.result}
            originallyScheduledDate={originallyScheduledDate}
            actionType="cancel"
          />
        ) : (
          <div className={css.confirmBox}>
            <form className={css.confirmForm} onSubmit={handleSubmit}>
              <label className={css.confirmLabel}>
                <span>{t('Reason', 'Reason')}</span>
                <textarea
                  className={css.multiLineInput}
                  placeholder={t(
                    'Reason_for_canceling_this_meeting',
                    'Reason for canceling this meeting',
                  )}
                  value={reason}
                  onChange={(evt) => setReason(evt.currentTarget.value)}
                />
              </label>
              <Button
                type="primary"
                disabled={(submitting && reason.length > 0) || bookingCanceled}
                onClick={handleSubmit}
                loading={submitting}
                color={brand_settings?.button_color}
              >
                {t('Cancel_Event', 'Cancel Event')}
              </Button>
              <button style={{display: 'none'}} />
            </form>
          </div>
        )}
      </BookingBody>
    </Container>
  );
};
